// src/components/Profile/ProfileDetails.js
import React, { useState, useEffect } from 'react';
import { TextField, Button, Box, Typography, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import './styles/ProfileDetails.css';

// Definizione delle materie
const subjects = [
  'Sostegno',
  'Italiano',
  'Storia',
  'Geografia',
  'Matematica',
  'Scienze',
  'Tecnologia',
  'Musica',
  'Arte e Immagine',
  'Inglese',
  'Educazione Civica',
  'Educazione Fisica',
  'Religione Cattolica',
];

// Definizione delle scuole
const schools = ['Infanzia', 'Primaria', 'Secondaria di primo grado'];

// Definizione delle classi in base alla scuola
const classesBySchool = {
  Infanzia: ['Prima', 'Seconda', 'Terza'],
  Primaria: ['Prima', 'Seconda', 'Terza', 'Quarta', 'Quinta'],
  'Secondaria di primo grado': ['Prima', 'Seconda', 'Terza'],
};

const ProfilePhoto = ({ isEditing }) => (
  <div className={`profile-photo ${isEditing ? 'hidden' : ''}`}>
    <div className="black-circle">
    <img src="/menu_icon/profile.png" alt="Profilo" className="profile-image" />
    </div>
  </div>
);

const InfoItem = ({ icon, label, value, name, isEditing }) => {
  return !isEditing ? (
    <Box className="info-box">
      <Typography className="info-text">{value}</Typography>
      <img src={icon} alt={label} className="icon-image" />
    </Box>
  ) : null;
};


const ProfileDetails = ({
  profileData,
  isEditing,
  updatedProfile,
  handleInputChange,
  handleSaveProfile,
  setIsEditing,
}) => {
  const [openTeachingSubjectMenu, setOpenTeachingSubjectMenu] = useState(false);
  const [openClassTypeMenu, setOpenClassTypeMenu] = useState(false);
  const [teachingSubjects, setTeachingSubjects] = useState([]);
  const [classTypes, setClassTypes] = useState([]);
  const [school, setSchool] = useState('');

  // Proprietà del menu per personalizzare gli stili
  const menuProps = {
    PaperProps: {
      sx: {
        '& .MuiMenuItem-root.Mui-selected': {
          backgroundColor: '#DF4634',
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: '#DF4634',
          },
        },
        '& .MuiMenuItem-root:hover': {
          backgroundColor: '#DF4634',
          color: '#FFFFFF',
        },
        '& .fatto-button': {
          display: 'flex',
          justifyContent: 'center',
          padding: '10px',
        },
      },
    },
    disableScrollLock: true,
  };

  // Inizializza i valori dai dati del profilo
  useEffect(() => {
    if (profileData) {
      // Gestisci stringhe separate da virgola convertendole in array
      const subjects = profileData.teaching_subject ? profileData.teaching_subject.split(', ') : [];
      const classes = profileData.current_class_type ? profileData.current_class_type.split(', ') : [];
      
      setTeachingSubjects(subjects);
      setClassTypes(classes);
      setSchool(profileData.school || '');

      // Aggiorna anche l'oggetto updatedProfile
      handleInputChange({
        target: { name: 'teaching_subject', value: subjects.join(', ') }
      });
      handleInputChange({
        target: { name: 'current_class_type', value: classes.join(', ') }
      });
      handleInputChange({
        target: { name: 'school', value: profileData.school || '' }
      });
    }
  }, [profileData]);

  // Gestisce il cambio di scuola
  const handleSchoolChange = (e) => {
    const selectedSchool = e.target.value;
    setSchool(selectedSchool);
    setClassTypes([]);
    
    handleInputChange({
      target: { name: 'school', value: selectedSchool }
    });
    handleInputChange({
      target: { name: 'current_class_type', value: '' }
    });
  };

  // Gestisce il cambio delle materie
  const handleSubjectsChange = (e) => {
    const selectedSubjects = e.target.value;
    setTeachingSubjects(selectedSubjects);
    
    handleInputChange({
      target: { name: 'teaching_subject', value: selectedSubjects.join(', ') }
    });
  };

  // Gestisce il cambio delle classi
  const handleClassesChange = (e) => {
    const selectedClasses = e.target.value;
    setClassTypes(selectedClasses);
    
    handleInputChange({
      target: { name: 'current_class_type', value: selectedClasses.join(', ') }
    });
  };

  return (
    <Box className="profile-box">
      <img
        src="/profile_icon/modify.png"
        alt="Modifica profilo"
        className="modify-icon"
        onClick={() => setIsEditing(!isEditing)}
      />

      <Box className="header-section">
        <Typography className="title">
          {profileData?.first_name} {profileData?.last_name}
        </Typography>
        {!isEditing && <ProfilePhoto isEditing={isEditing} />}
      </Box>

      {isEditing ? (
        <Box className="edit-section">
          <TextField
            label="Nome"
            name="first_name"
            value={updatedProfile.first_name || ''}
            onChange={handleInputChange}
            fullWidth
            className="edit-textfield"
            variant="outlined"
          />
          <TextField
            label="Cognome"
            name="last_name"
            value={updatedProfile.last_name || ''}
            onChange={handleInputChange}
            fullWidth
            className="edit-textfield"
            variant="outlined"
          />
          
          {/* Dropdown per la scuola */}
          <FormControl className="edit-dropdown" fullWidth variant="outlined">
            <InputLabel>Scuola</InputLabel>
            <Select
              value={school}
              onChange={handleSchoolChange}
              label="Scuola"
              MenuProps={menuProps}
              className="select-field"
            >
              {schools.map((schoolOption) => (
                <MenuItem key={schoolOption} value={schoolOption}>
                  {schoolOption}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {/* Dropdown per le classi */}
          <FormControl className="edit-dropdown" fullWidth variant="outlined" disabled={!school}>
            <InputLabel>Classi</InputLabel>
            <Select
              multiple
              value={classTypes}
              onChange={handleClassesChange}
              label="Classi"
              renderValue={(selected) => selected.join(', ')}
              MenuProps={{
                ...menuProps,
                open: openClassTypeMenu,
                onClose: () => setOpenClassTypeMenu(false),
              }}
              open={openClassTypeMenu}
              onOpen={() => setOpenClassTypeMenu(true)}
              className="select-field"
            >
              {school &&
                classesBySchool[school].map((classType) => (
                  <MenuItem key={classType} value={classType}>
                    {classType}
                  </MenuItem>
                ))}
              {/* Pulsante "Fatto" nella dropdown */}
              <MenuItem disableGutters className="fatto-button">
                <Button
                  onClick={() => setOpenClassTypeMenu(false)}
                  variant="contained"
                  fullWidth
                  sx={{
                    backgroundColor: '#DF4634',
                    color: '#FFFFFF',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#DF4634',
                    },
                  }}
                >
                  Fatto
                </Button>
              </MenuItem>
            </Select>
          </FormControl>
          
          {/* Dropdown per le materie */}
          <FormControl className="edit-dropdown" fullWidth variant="outlined">
            <InputLabel>Materie</InputLabel>
            <Select
              multiple
              value={teachingSubjects}
              onChange={handleSubjectsChange}
              label="Materie"
              renderValue={(selected) => selected.join(', ')}
              MenuProps={{
                ...menuProps,
                open: openTeachingSubjectMenu,
                onClose: () => setOpenTeachingSubjectMenu(false),
              }}
              open={openTeachingSubjectMenu}
              onOpen={() => setOpenTeachingSubjectMenu(true)}
              className="select-field"
            >
              {subjects.map((subject) => (
                <MenuItem key={subject} value={subject}>
                  {subject}
                </MenuItem>
              ))}
              {/* Pulsante "Fatto" nella dropdown */}
              <MenuItem disableGutters className="fatto-button">
                <Button
                  onClick={() => setOpenTeachingSubjectMenu(false)}
                  variant="contained"
                  fullWidth
                  sx={{
                    backgroundColor: '#DF4634',
                    color: '#FFFFFF',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#DF4634',
                    },
                  }}
                >
                  Fatto
                </Button>
              </MenuItem>
            </Select>
          </FormControl>
          
          <Button
            variant="contained"
            onClick={handleSaveProfile}
            className="save-button"
          >
            Salva
          </Button>
        </Box>
      ) : (
        <Box className="info-section">
          <InfoItem
            icon="/profile_icon//school.png"
            label="Scuola"
            name="school"
            value={profileData?.school}
            isEditing={isEditing}
          />
          <InfoItem
            icon="/profile_icon//class.png"
            label="Classi"
            name="current_class_type"
            value={profileData?.current_class_type}
            isEditing={isEditing}
          />
          <InfoItem
            icon="/profile_icon//cover.png"
            label="Materie"
            name="teaching_subject"
            value={profileData?.teaching_subject}
            isEditing={isEditing}
          />
        </Box>
      )}
    </Box>
  );
};

export default ProfileDetails;
